const CLIENT_ID = 'engage'
const STATE_PARAM = 'sso_state'
const STATE_STORAGE_KEY = 'engage.sso_state'

const portalUrl = () => process.env.VUE_APP_AUTH_PORTAL_URL || ''

export function isPortalEnabled(): boolean {
  return !!portalUrl()
}

function isRelativePath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
}

function sameOriginPath(url: URL): string {
  if (url.origin !== window.location.origin || url.pathname.startsWith('//') || url.pathname.startsWith('/\\')) {
    return '/'
  }
  return url.pathname
}

function safeIntended(path: string | undefined | null): string {
  if (!isRelativePath(path)) {
    return '/'
  }
  return sameOriginPath(new URL(path, window.location.origin))
}

function newState(): string {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

function withState(intended: string, state: string): string {
  const url = new URL(intended, window.location.origin)
  url.searchParams.set(STATE_PARAM, state)
  return url.pathname + url.search + url.hash
}

export function takeVerifiedIntended(returnedIntended: unknown): string | null {
  const expected = window.sessionStorage.getItem(STATE_STORAGE_KEY)
  window.sessionStorage.removeItem(STATE_STORAGE_KEY)
  if (!expected || !isRelativePath(returnedIntended)) {
    return null
  }
  const url = new URL(returnedIntended, window.location.origin)
  if (url.searchParams.get(STATE_PARAM) !== expected) {
    return null
  }
  return sameOriginPath(url)
}

export function portalLoginUrl(intended?: string, forcePrompt = false): string {
  const state = newState()
  window.sessionStorage.setItem(STATE_STORAGE_KEY, state)
  const url = new URL('login', portalUrl())
  url.searchParams.set('client', CLIENT_ID)
  url.searchParams.set('intended', withState(safeIntended(intended), state))
  url.searchParams.set('return_origin', window.location.origin)
  if (forcePrompt) {
    url.searchParams.set('prompt', 'login')
  }
  return url.toString()
}

export function portalLogoutUrl(): string {
  const url = new URL('logout', portalUrl())
  url.searchParams.set('client', CLIENT_ID)
  return url.toString()
}

export function redirectToPortalLogin(intended?: string, forcePrompt = false) {
  window.location.assign(portalLoginUrl(intended, forcePrompt))
}

export async function exchangeHandoffCode(code: string): Promise<string> {
  if (!isPortalEnabled()) {
    throw new Error('portal_disabled')
  }
  const response = await fetch(new URL('engage/token', portalUrl()).toString(), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok || typeof body.accessToken !== 'string') {
    throw new Error(body.error || 'engage_token_failed')
  }
  return body.accessToken
}
